import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import EditMode from '@/components/ai/EditMode';
import ReadMode from '@/components/ai/ReadMode';
import { type Filter } from '@/models/Filter';
import { type Locale } from '@/i18n';
import useAISearch from '@/hooks/useAISearch';
import useSpinner from '@/hooks/useSpinner';
import type { AIStrings } from '@/utils/filters/options/getFilterOptions';
import { useOnOutsideClick } from '@cvent/carina/hooks/useOnOutsideClick';

type Props = {
  children: ReactNode;
  searchToggle: ReactNode;
  filter: Filter;
  locale: Locale;
  basePath: string;
  isLoggedIn: boolean;
  searchTerm: string;
  strings: AIStrings;
  sortBy: ReactNode;
};

export default function AISearchContainer({
  searchTerm,
  isLoggedIn,
  locale,
  basePath,
  filter,
  searchToggle,
  strings,
  children,
  sortBy
}: Props): JSX.Element {
  // NOTE -
  // query - what is passed as an input to prompt
  // summary - the text we show on read mode
  const [query, setQuery] = useState<string[]>([filter.SUMMARY ?? '']);
  const [summary, setSummary] = useState('');

  const [editMode, setEditMode] = useState(false);
  const [triggerSearch, setTriggerSearch] = useState(false);
  const [error, setError] = useState<{ error: boolean; text: string }>({ error: false, text: '' });
  const initiateSearch = useAISearch(searchTerm, isLoggedIn, locale, basePath);
  const { showSpinner, setShowSpinner } = useSpinner();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (filter.SUMMARY && summary !== filter.SUMMARY) {
      setSummary(filter.SUMMARY);
    }
    setEditMode(false);

    // reset triggerSearch
    if (triggerSearch) {
      setTriggerSearch(false);
    }

    if (showSpinner) {
      setShowSpinner(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    async function callAI() {
      if (query.length > 0 && triggerSearch) {
        setShowSpinner(true);
        const response = await initiateSearch(query);

        if (response.err && response.errorType === 'LOCATION_NOT_FOUND') {
          // when query parser was not able to find the location
          setError({ error: true, text: strings.locationNotFound });
        } else if (response.err) {
          // generic error
          setError({ error: true, text: strings.aiErrorText });
        }

        if (!response.routed) {
          // reset defaults
          setShowSpinner(false);

          if (triggerSearch) {
            setTriggerSearch(false);
          }

          setEditMode(false);
        }
        // setting user input for read mode in case of error
        setSummary(query[0]);
      }
    }

    callAI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, triggerSearch]);

  const onOpenEdit = useCallback(() => {
    if (!editMode) {
      setEditMode(true);
      setQuery([summary]);
      if (error) {
        setError({ error: false, text: '' });
      }
    }
  }, [editMode, error, summary]);

  // clicking outside search exits edit mode
  useOnOutsideClick(
    [ref],
    () => {
      setEditMode(false);
    },
    editMode
  );

  return (
    <div className={`flex w-full flex-col ${error.error ? 'gap-2 py-0' : 'gap-4 py-2'} px-2`}>
      <div>
        <div className="flex flex-row items-center justify-between">
          {/* filters */}
          <div className="flex">{children}</div>

          <div className="flex flex-grow flex-col">
            {/* AI search bar */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
            <div
              ref={ref}
              tabIndex={0}
              role="button"
              onClick={onOpenEdit}
              className={`flex-grow animate-border rounded-[30px] border-1 border-neutral-30 ${
                editMode
                  ? 'animate-border bg-gradient-to-r from-[#8B93FF] via-[#5755FE] to-[#FF71CD] bg-[length:400%_400%]'
                  : ''
              } p-[2px]`}
            >
              <div className="flex w-full flex-row justify-between rounded-[26px] bg-background">
                {editMode ? (
                  <EditMode
                    strings={strings}
                    query={query}
                    error={error}
                    setError={setError}
                    setQuery={setQuery}
                    setTriggerSearch={setTriggerSearch}
                    locale={locale}
                  />
                ) : (
                  <ReadMode
                    error={error}
                    setError={setError}
                    onOpenEdit={onOpenEdit}
                    setQuery={setQuery}
                    summary={summary}
                    setTriggerSearch={setTriggerSearch}
                    strings={strings}
                  />
                )}
              </div>
            </div>
            <div>
              {/* error message */}
              {error.error && <div className="ml-2 pt-1 text-[12px] text-neutral-60">{error.text}</div>}
            </div>
          </div>
          {/* toggle */}
          {searchToggle}
          <div className="hidden lg:flex">{sortBy}</div>
        </div>
      </div>
    </div>
  );
}
